/**
 * Sistema de Mensagens de Erro Padronizadas
 * 
 * Este utilitário centraliza todas as mensagens de erro do sistema,
 * garantindo que informações sensíveis não sejam expostas ao usuário.
 */

export enum ErrorType {
  // Autenticação
  AUTH_REQUIRED = 'AUTH_REQUIRED',
  AUTH_INVALID = 'AUTH_INVALID',
  AUTH_EXPIRED = 'AUTH_EXPIRED',
  AUTH_FORBIDDEN = 'AUTH_FORBIDDEN',
  
  // Validação
  VALIDATION_REQUIRED = 'VALIDATION_REQUIRED',
  VALIDATION_INVALID = 'VALIDATION_INVALID',
  VALIDATION_DATE_RANGE = 'VALIDATION_DATE_RANGE',
  VALIDATION_MIN_VALUE = 'VALIDATION_MIN_VALUE',
  VALIDATION_MAX_VALUE = 'VALIDATION_MAX_VALUE',
  
  // Recursos
  RESOURCE_NOT_FOUND = 'RESOURCE_NOT_FOUND',
  RESOURCE_ALREADY_EXISTS = 'RESOURCE_ALREADY_EXISTS',
  RESOURCE_UNAVAILABLE = 'RESOURCE_UNAVAILABLE',
  
  // Operações
  OPERATION_FAILED = 'OPERATION_FAILED',
  OPERATION_TIMEOUT = 'OPERATION_TIMEOUT',
  OPERATION_NOT_ALLOWED = 'OPERATION_NOT_ALLOWED',
  
  // Servidor
  SERVER_ERROR = 'SERVER_ERROR',
  NETWORK_ERROR = 'NETWORK_ERROR',
  DATABASE_ERROR = 'DATABASE_ERROR',
  
  // Upload
  UPLOAD_FAILED = 'UPLOAD_FAILED',
  UPLOAD_INVALID_TYPE = 'UPLOAD_INVALID_TYPE',
  UPLOAD_TOO_LARGE = 'UPLOAD_TOO_LARGE',
}

interface ErrorMessage {
  title: string;
  description: string;
  variant?: 'default' | 'destructive';
}

/**
 * Mapeamento de tipos de erro para mensagens amigáveis
 * NUNCA expõe detalhes técnicos ou informações sensíveis
 */
const ERROR_MESSAGES: Record<ErrorType, ErrorMessage> = {
  // Autenticação
  [ErrorType.AUTH_REQUIRED]: {
    title: 'Autenticação necessária',
    description: 'Por favor, faça login para continuar.',
    variant: 'destructive',
  },
  [ErrorType.AUTH_INVALID]: {
    title: 'Credenciais inválidas',
    description: 'Email ou senha incorretos. Verifique suas credenciais e tente novamente.',
    variant: 'destructive',
  },
  [ErrorType.AUTH_EXPIRED]: {
    title: 'Sessão expirada',
    description: 'Sua sessão expirou. Por favor, faça login novamente.',
    variant: 'destructive',
  },
  [ErrorType.AUTH_FORBIDDEN]: {
    title: 'Acesso negado',
    description: 'Você não tem permissão para realizar esta ação.',
    variant: 'destructive',
  },
  
  // Validação
  [ErrorType.VALIDATION_REQUIRED]: {
    title: 'Campos obrigatórios',
    description: 'Por favor, preencha todos os campos obrigatórios.',
    variant: 'destructive',
  },
  [ErrorType.VALIDATION_INVALID]: {
    title: 'Dados inválidos',
    description: 'Alguns dados informados são inválidos. Verifique e tente novamente.',
    variant: 'destructive',
  },
  [ErrorType.VALIDATION_DATE_RANGE]: {
    title: 'Datas inválidas',
    description: 'A data de check-out deve ser posterior à data de check-in.',
    variant: 'destructive',
  },
  [ErrorType.VALIDATION_MIN_VALUE]: {
    title: 'Valor mínimo não atingido',
    description: 'O valor informado é menor que o mínimo permitido.',
    variant: 'destructive',
  },
  [ErrorType.VALIDATION_MAX_VALUE]: {
    title: 'Valor máximo excedido',
    description: 'O valor informado excede o máximo permitido.',
    variant: 'destructive',
  },
  
  // Recursos
  [ErrorType.RESOURCE_NOT_FOUND]: {
    title: 'Recurso não encontrado',
    description: 'O item solicitado não foi encontrado.',
    variant: 'destructive',
  },
  [ErrorType.RESOURCE_ALREADY_EXISTS]: {
    title: 'Item já existe',
    description: 'Este item já está cadastrado no sistema.',
    variant: 'destructive',
  },
  [ErrorType.RESOURCE_UNAVAILABLE]: {
    title: 'Recurso indisponível',
    description: 'Este recurso não está disponível no momento.',
    variant: 'destructive',
  },
  
  // Operações
  [ErrorType.OPERATION_FAILED]: {
    title: 'Operação não concluída',
    description: 'Não foi possível concluir a operação. Tente novamente mais tarde.',
    variant: 'destructive',
  },
  [ErrorType.OPERATION_TIMEOUT]: {
    title: 'Tempo esgotado',
    description: 'A operação demorou muito para ser concluída. Tente novamente.',
    variant: 'destructive',
  },
  [ErrorType.OPERATION_NOT_ALLOWED]: {
    title: 'Operação não permitida',
    description: 'Esta operação não é permitida no momento.',
    variant: 'destructive',
  },
  
  // Servidor
  [ErrorType.SERVER_ERROR]: {
    title: 'Erro no servidor',
    description: 'Ocorreu um erro interno. Nossa equipe foi notificada e está trabalhando para resolver.',
    variant: 'destructive',
  },
  [ErrorType.NETWORK_ERROR]: {
    title: 'Erro de conexão',
    description: 'Não foi possível conectar ao servidor. Verifique sua conexão com a internet.',
    variant: 'destructive',
  },
  [ErrorType.DATABASE_ERROR]: {
    title: 'Erro no banco de dados',
    description: 'Ocorreu um erro ao processar sua solicitação. Tente novamente mais tarde.',
    variant: 'destructive',
  },
  
  // Upload
  [ErrorType.UPLOAD_FAILED]: {
    title: 'Falha no upload',
    description: 'Não foi possível fazer o upload do arquivo. Tente novamente.',
    variant: 'destructive',
  },
  [ErrorType.UPLOAD_INVALID_TYPE]: {
    title: 'Tipo de arquivo inválido',
    description: 'O tipo de arquivo não é permitido. Verifique o formato e tente novamente.',
    variant: 'destructive',
  },
  [ErrorType.UPLOAD_TOO_LARGE]: {
    title: 'Arquivo muito grande',
    description: 'O arquivo excede o tamanho máximo permitido.',
    variant: 'destructive',
  },
};

/**
 * Obtém uma mensagem de erro padronizada baseada no tipo
 */
export const getErrorMessage = (errorType: ErrorType): ErrorMessage => {
  return ERROR_MESSAGES[errorType] || ERROR_MESSAGES[ErrorType.OPERATION_FAILED];
};

/**
 * Analisa um erro e retorna o tipo apropriado
 * NUNCA expõe detalhes técnicos do erro original
 */
export const parseError = (error: unknown): ErrorType => {
  // Se já for um ErrorType, retornar diretamente
  if (typeof error === 'string' && Object.values(ErrorType).includes(error as ErrorType)) {
    return error as ErrorType;
  }
  
  // Se for um objeto Error, analisar a mensagem (sem expor detalhes)
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    
    // Autenticação
    if (message.includes('unauthorized') || message.includes('token') || message.includes('authentication')) {
      return ErrorType.AUTH_INVALID;
    }
    if (message.includes('forbidden') || message.includes('permission') || message.includes('access denied')) {
      return ErrorType.AUTH_FORBIDDEN;
    }
    
    // Validação
    if (message.includes('required') || message.includes('obrigatório')) {
      return ErrorType.VALIDATION_REQUIRED;
    }
    if (message.includes('invalid') || message.includes('inválido')) {
      return ErrorType.VALIDATION_INVALID;
    }
    
    // Recursos
    if (message.includes('not found') || message.includes('não encontrado')) {
      return ErrorType.RESOURCE_NOT_FOUND;
    }
    if (message.includes('already exists') || message.includes('já existe')) {
      return ErrorType.RESOURCE_ALREADY_EXISTS;
    }
    
    // Rede
    if (message.includes('network') || message.includes('fetch') || message.includes('connection')) {
      return ErrorType.NETWORK_ERROR;
    }
    
    // Upload
    if (message.includes('upload') || message.includes('file')) {
      return ErrorType.UPLOAD_FAILED;
    }
  }
  
  // Erro HTTP
  if (typeof error === 'object' && error !== null && 'status' in error) {
    const status = (error as { status: number }).status;
    
    if (status === 401) return ErrorType.AUTH_INVALID;
    if (status === 403) return ErrorType.AUTH_FORBIDDEN;
    if (status === 404) return ErrorType.RESOURCE_NOT_FOUND;
    if (status === 409) return ErrorType.RESOURCE_ALREADY_EXISTS;
    if (status >= 500) return ErrorType.SERVER_ERROR;
    if (status === 0 || status >= 500) return ErrorType.NETWORK_ERROR;
  }
  
  // Padrão: erro genérico
  return ErrorType.OPERATION_FAILED;
};

/**
 * Cria uma mensagem de erro personalizada com contexto específico
 * (mas ainda sem expor detalhes técnicos)
 */
export const createCustomErrorMessage = (
  errorType: ErrorType,
  customDescription?: string
): ErrorMessage => {
  const baseMessage = getErrorMessage(errorType);
  return {
    ...baseMessage,
    description: customDescription || baseMessage.description,
  };
};

